import { useCallback, useState } from "react";

import { AuthenticatedAppShell } from "../../../components/authenticated/AuthenticatedAppShell";
import type { AuthenticatedShellSection } from "../../../components/authenticated/AuthenticatedAppShell";
import { useBookingPolicySurface } from "../hooks/useBookingPolicySurface";
import { useCustomersSurface } from "../hooks/useCustomersSurface";
import { useSetupAgenda } from "../hooks/useSetupAgenda";
import { useShellBoot } from "../hooks/useShellBoot";
import { formatSectionTitle } from "../domain/shellFormatters";
import type { AuthenticatedShellPageProps } from "../domain/shellTypes";
import { AuthenticatedAppGate } from "./AuthenticatedAppGate";
import { ShellContent } from "./ShellContent";

const staffVisibleSections: AuthenticatedShellSection[] = ["today", "agenda", "customers"];

export function AuthenticatedShellPage(props: AuthenticatedShellPageProps) {
  const [activeSection, setActiveSection] = useState<AuthenticatedShellSection>(props.initialActiveSection ?? "today");
  const onStaffBoot = useCallback(() => setActiveSection("today"), []);
  const onLogoutReset = useCallback(() => setActiveSection("agenda"), []);
  const {
    apiClient,
    hasInitialBoot,
    logoutDisabled,
    manualToken,
    sessionToken,
    setManualToken,
    state,
    handleLogout,
    handleSubmit
  } = useShellBoot({
    initialAccessToken: props.initialAccessToken,
    initialBoot: props.initialBoot,
    onLogoutReset,
    onStaffBoot
  });

  const isProfessionalUser = state.status === "success" && state.boot.user.role === "professional";
  const isStaffUser = state.status === "success" && state.boot.user.role === "staff";
  const effectiveActiveSection: AuthenticatedShellSection = isProfessionalUser
    ? "today"
    : isStaffUser && !staffVisibleSections.includes(activeSection)
      ? "today"
      : activeSection;

  const { agendaState, resetSetupAgenda, setupState } = useSetupAgenda({
    apiClient,
    initialAccessToken: props.initialAccessToken,
    initialAgendaError: props.initialAgendaError,
    initialAppointments: props.initialAppointments,
    initialSetup: props.initialSetup,
    isProfessionalUser,
    sessionToken,
    state
  });
  const { customersState, resetCustomers } = useCustomersSurface({
    activeSection: effectiveActiveSection,
    apiClient,
    initialCustomerDetail: props.initialCustomerDetail,
    initialCustomerHistory: props.initialCustomerHistory,
    initialCustomerNotes: props.initialCustomerNotes,
    initialCustomers: props.initialCustomers,
    initialCustomersError: props.initialCustomersError,
    isProfessionalUser,
    sessionToken,
    state
  });
  const { bookingPolicyState, resetBookingPolicy } = useBookingPolicySurface({
    activeSection: effectiveActiveSection,
    apiClient,
    initialBookingPolicy: props.initialBookingPolicy,
    initialBookingPolicyError: props.initialBookingPolicyError,
    isProfessionalUser,
    isStaffUser,
    sessionToken,
    state
  });

  const resetAuthenticatedSurfaces = useCallback(() => {
    resetSetupAgenda();
    resetCustomers();
    resetBookingPolicy();
  }, [resetBookingPolicy, resetCustomers, resetSetupAgenda]);

  function handleSectionChange(section: AuthenticatedShellSection) {
    if (isProfessionalUser && section !== "today") {
      return;
    }
    if (isStaffUser && !staffVisibleSections.includes(section)) {
      return;
    }
    setActiveSection(section);
  }

  if (!hasInitialBoot && !sessionToken && state.status !== "success") {
    return <AuthenticatedAppGate state={state} />;
  }

  return (
    <AuthenticatedAppShell
      title={state.status === "success" ? formatSectionTitle(effectiveActiveSection) : "Area de trabalho"}
      status={state.status === "unauthenticated" ? "idle" : state.status}
      activeSection={effectiveActiveSection}
      visibleSections={isProfessionalUser ? ["today"] : isStaffUser ? staffVisibleSections : undefined}
      showSessionForm={false}
      sessionToken={manualToken}
      sessionLoading={state.status === "loading"}
      onSectionChange={handleSectionChange}
      onSessionTokenChange={setManualToken}
      onSessionSubmit={handleSubmit}
      onLogout={sessionToken ? () => void handleLogout().then(resetAuthenticatedSurfaces) : undefined}
      logoutDisabled={logoutDisabled}
      userEmail={state.status === "success" ? state.boot.user.email : undefined}
      whatsAppConnected={false}
    >
      <ShellContent
        state={state}
        setupState={setupState}
        agendaState={agendaState}
        activeSection={effectiveActiveSection}
        customersState={customersState}
        bookingPolicyState={bookingPolicyState}
      />
    </AuthenticatedAppShell>
  );
}
